import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { User } from '@/models/user.model'
import { toUser } from '@/models/mappers/user.mapper'
import { login as loginRequest } from '@/services/auth.service'
import { AUTH_EVENTS, STORAGE_KEYS } from '@/constants'

interface AuthContextValue {
  user: User | null
  accessToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  /** Updates the cached session user (e.g. after a successful profile edit). */
  updateUser: (user: User) => void
}

interface StoredSession {
  accessToken: string
  refreshToken: string
  expiresAt: number
  user: User
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function loadSession(): StoredSession | null {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    const refreshToken = localStorage.getItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN)
    const expiresAt = localStorage.getItem(STORAGE_KEYS.AUTH_EXPIRES_AT)
    const userJson = localStorage.getItem(STORAGE_KEYS.AUTH_USER)

    if (!token || !refreshToken || !expiresAt || !userJson) return null
    if (Date.now() >= Number(expiresAt)) return null

    return {
      accessToken: token,
      refreshToken,
      expiresAt: Number(expiresAt),
      user: JSON.parse(userJson) as User,
    }
  } catch {
    return null
  }
}

function saveSession(token: string, refresh: string, expiresIn: number, user: User): void {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
  localStorage.setItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN, refresh)
  localStorage.setItem(STORAGE_KEYS.AUTH_EXPIRES_AT, String(Date.now() + expiresIn * 1000))
  localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user))
}

function updateStoredUser(user: User): void {
  localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user))
}

function clearSession(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.AUTH_EXPIRES_AT)
  localStorage.removeItem(STORAGE_KEYS.AUTH_USER)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Restore session from localStorage on mount
  useEffect(() => {
    const session = loadSession()
    if (session) {
      setUser(session.user)
      setAccessToken(session.accessToken)
    }
    setIsLoading(false)
  }, [])

  // Listen for events emitted by the Axios interceptor so session state
  // stays in sync when the interceptor refreshes or invalidates the token.
  useEffect(() => {
    function handleForceLogout(): void {
      clearSession()
      setUser(null)
      setAccessToken(null)
    }

    function handleTokenRefreshed(event: Event): void {
      const { accessToken: newToken } = (
        event as CustomEvent<{ accessToken: string }>
      ).detail
      setAccessToken(newToken)
    }

    window.addEventListener(AUTH_EVENTS.LOGOUT, handleForceLogout)
    window.addEventListener(AUTH_EVENTS.TOKEN_REFRESHED, handleTokenRefreshed)

    return () => {
      window.removeEventListener(AUTH_EVENTS.LOGOUT, handleForceLogout)
      window.removeEventListener(AUTH_EVENTS.TOKEN_REFRESHED, handleTokenRefreshed)
    }
  }, [])

  async function login(username: string, password: string): Promise<void> {
    const response = await loginRequest({ username, password })
    const mappedUser = toUser(response.user)
    saveSession(response.access_token, response.refresh_token, response.expires_in, mappedUser)
    setUser(mappedUser)
    setAccessToken(response.access_token)
  }

  function logout(): void {
    clearSession()
    setUser(null)
    setAccessToken(null)
  }

  function updateUser(updated: User): void {
    updateStoredUser(updated)
    setUser(updated)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
